import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Card, Col, DatePicker, Dropdown, Empty, Modal, Row, Select, Spin, Table, Tag, Typography, message,
} from "antd";
import {
    AuditOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, StopOutlined,
    EyeOutlined, MoreOutlined,
} from "@ant-design/icons";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import axiosInstance from "../../variables/api/axiosInstance";
import { displayNumbers } from "../../variables/numbersFunctions";
import { frenchDate } from "../../variables/functions";
import { validateEvaluation, rejectEvaluation } from "../../hooks/useHooks";
import { LOGGED_USER } from "../../variables/constants";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const PRIMARY = "#242166";
const { confirm } = Modal;

const ETAT = {
    VALIDE: { label: "Validée", color: "success" },
    EN_ATTENTE: { label: "En attente", color: "processing" },
    REJETE: { label: "Rejetée", color: "error" },
};
const etatTag = (e) => {
    const m = ETAT[e] || { label: e || "—", color: "default" };
    return <Tag color={m.color} style={{ borderRadius: 6 }}>{m.label}</Tag>;
};

/**
 * Listing des évaluations (v2) — consultation moderne de toutes les évaluations, avec
 * filtre de dates SERVEUR (startDate/endDate), compteurs par statut, et actions
 * valider / rejeter / voir le sinistre.
 */
const ValidationEvaluationsV2 = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [periode, setPeriode] = useState(null);
    const [counts, setCounts] = useState({});
    const [reloadTick, setReloadTick] = useState(0);
    // Filtres compagnie (POOL uniquement — une compagnie est déjà scopée à son organisme) + branche.
    const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
    const [compagnie, setCompagnie] = useState(null);
    const [compagnies, setCompagnies] = useState([]);
    const [branche, setBranche] = useState(null);
    const [branches, setBranches] = useState([]);

    // Options des filtres : compagnies (Pool seul) + branches (déjà filtrées par visibilité côté serveur).
    useEffect(() => {
        if (isPool) {
            axiosInstance.get("/api/organisme/all/assurances-pool")
                .then((r) => setCompagnies(Array.isArray(r.data) ? r.data : []))
                .catch(() => setCompagnies([]));
        }
        axiosInstance.get("/api/branche")
            .then((r) => setBranches(Array.isArray(r.data) ? r.data : []))
            .catch(() => setBranches([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtreParams = () => {
        const p = {};
        if (periode?.length === 2) {
            p.startDate = periode[0].format("YYYY-MM-DD");
            p.endDate = periode[1].format("YYYY-MM-DD");
        }
        if (compagnie) p.compagnie = compagnie;
        if (branche) p.branche = branche;
        return p;
    };

    useEffect(() => {
        setLoading(true);
        const params = { page: page - 1, size: pageSize, ...filtreParams() };
        axiosInstance.get(`/api/evaluation/sinistre`, { params })
            .then((res) => {
                const { content = [], totalElements = 0 } = res.data || {};
                setRows(content);
                setTotal(totalElements);
            })
            .catch((e) => console.log(e))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, periode, compagnie, branche, reloadTick]);

    useEffect(() => {
        axiosInstance.get(`/api/evaluation/statut-count`, { params: filtreParams() })
            .then((r) => setCounts(r.data || {})).catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periode, compagnie, branche, reloadTick]);

    const data = useMemo(() => (rows || []).map((ev) => {
        const e = ev?.evaluation || {};
        return {
            key: e.codeId,
            codeId: e.codeId,
            sinistreCodeId: ev?.sinistreCodeId,
            numSinistre: ev?.sinistreNumSinistre,
            numPolice: ev?.sinistreNumPolice,
            souscripteur: ev?.sinistreSouscripteur,
            ancienneProvision: e?.ancienneProvision,
            nouvelleProvision: e?.nouvelleProvision,
            etatEvaluation: e?.etatEvaluation,
            courant: ev?.courant,
            date: e?.createdAt ? frenchDate(e.createdAt) : "",
        };
    }), [rows]);

    const refresh = () => setReloadTick((t) => t + 1);

    const handleValider = (record) => {
        confirm({
            title: "Valider cette évaluation ?", icon: <CheckCircleOutlined style={{ color: "#16a34a" }} />,
            okText: "Valider", cancelText: "Annuler", okButtonProps: { style: { background: "#16a34a", borderColor: "#16a34a" } },
            onOk: () => validateEvaluation(record.codeId).then(() => { message.success("Évaluation validée"); refresh(); })
                .catch(() => message.error("Échec de la validation")),
        });
    };
    const handleRejeter = (record) => {
        confirm({
            title: "Rejeter cette évaluation ?", icon: <CloseCircleOutlined style={{ color: "#dc2626" }} />,
            okText: "Rejeter", okType: "danger", cancelText: "Annuler",
            onOk: () => rejectEvaluation(record.codeId).then(() => { message.success("Évaluation rejetée"); refresh(); })
                .catch(() => message.error("Échec du rejet")),
        });
    };
    const voirSinistre = (record) => {
        if (record.sinistreCodeId) navigate("/details-sinistre/v2", { state: { codeId: record.sinistreCodeId } });
    };

    const menuItems = (record) => {
        const items = [{ key: "voir", icon: <EyeOutlined />, label: "Voir le sinistre" }];
        if (record.etatEvaluation === "EN_ATTENTE") {
            items.push({ key: "valider", icon: <CheckCircleOutlined style={{ color: "#16a34a" }} />, label: "Valider" });
            items.push({ key: "rejeter", icon: <CloseCircleOutlined style={{ color: "#dc2626" }} />, label: "Rejeter" });
        } else if (record.etatEvaluation === "VALIDE") {
            items.push({ key: "rejeter", icon: <CloseCircleOutlined style={{ color: "#dc2626" }} />, label: "Rejeter" });
        }
        return items;
    };
    const onMenuClick = (key, record) => {
        if (key === "voir") voirSinistre(record);
        else if (key === "valider") handleValider(record);
        else if (key === "rejeter") handleRejeter(record);
    };

    const kpi = (label, value, color, icon) => (
        <Card size="small" bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 36, height: 36, borderRadius: 9, background: `${color}14`, color, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{icon}</span>
                <div><div style={{ fontSize: 12, color: "#6b7280" }}>{label}</div><div style={{ fontSize: 20, fontWeight: 700, color }}>{displayNumbers(value || 0)}</div></div>
            </div>
        </Card>
    );

    const columns = [
        { title: "N° sinistre", dataIndex: "numSinistre", key: "numSinistre", render: (v, r) => <div><div style={{ fontWeight: 600, color: "#111827" }}>{v || "—"}</div><div style={{ fontSize: 12, color: "#9ca3af" }}>{r.numPolice}</div></div> },
        { title: "Souscripteur", dataIndex: "souscripteur", key: "souscripteur", render: (v) => v || "—" },
        { title: "Ancienne prov.", dataIndex: "ancienneProvision", key: "anc", align: "right", render: (v) => <span style={{ color: "#6b7280" }}>{displayNumbers(v || 0)} F</span> },
        { title: "Nouvelle prov.", dataIndex: "nouvelleProvision", key: "nouv", align: "right", render: (v) => <strong style={{ color: PRIMARY }}>{displayNumbers(v || 0)} F</strong> },
        { title: "État", dataIndex: "etatEvaluation", key: "etat", align: "center", render: (e) => etatTag(e) },
        { title: "Courante", dataIndex: "courant", key: "courant", align: "center", render: (c) => (c ? <Tag color="blue" style={{ borderRadius: 6 }}>Courante</Tag> : <Text type="secondary">—</Text>) },
        { title: "Date", dataIndex: "date", key: "date", render: (v) => <span><ClockCircleOutlined style={{ marginRight: 6, color: "#9ca3af" }} />{v}</span> },
        {
            title: "", key: "action", align: "center", width: 50, render: (_, r) => (
                <Dropdown trigger={["click"]} menu={{ items: menuItems(r), onClick: (e) => onMenuClick(e.key, r) }}>
                    <Button type="text" icon={<MoreOutlined />} style={{ fontSize: 18 }} />
                </Dropdown>
            ),
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            {/* En-tête */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#111827" }}><AuditOutlined style={{ marginRight: 8, color: PRIMARY }} />Listing des évaluations</Title>
                                    <Text type="secondary">Consultation et traitement des évaluations de sinistres</Text>
                                </div>
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                                    {isPool && (
                                        <Select
                                            allowClear
                                            showSearch
                                            style={{ minWidth: 200 }}
                                            placeholder="Toutes les compagnies"
                                            optionFilterProp="label"
                                            value={compagnie || undefined}
                                            onChange={(v) => { setCompagnie(v || null); setPage(1); }}
                                            options={compagnies.map((c) => ({ value: c.code, label: c.raisonSociale || c.code }))}
                                        />
                                    )}
                                    <Select
                                        allowClear
                                        showSearch
                                        style={{ minWidth: 200 }}
                                        placeholder="Toutes les branches"
                                        optionFilterProp="label"
                                        value={branche || undefined}
                                        onChange={(v) => { setBranche(v || null); setPage(1); }}
                                        options={branches.map((b) => ({ value: b.codeBranche, label: b.nom ? `${b.nom} (${b.codeBranche})` : b.codeBranche }))}
                                    />
                                    <RangePicker
                                        value={periode}
                                        onChange={(d) => { setPeriode(d); setPage(1); }}
                                        format="DD/MM/YYYY"
                                        placeholder={["Début", "Fin"]}
                                        allowClear
                                    />
                                </div>
                            </div>

                            <Spin spinning={loading}>
                                {/* KPIs par statut */}
                                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                                    <Col xs={12} md={6}>{kpi("En attente", counts?.attenteCount, "#2563eb", <ClockCircleOutlined />)}</Col>
                                    <Col xs={12} md={6}>{kpi("Validées", counts?.valideCount, "#16a34a", <CheckCircleOutlined />)}</Col>
                                    <Col xs={12} md={6}>{kpi("Rejetées", counts?.rejeteCount, "#dc2626", <CloseCircleOutlined />)}</Col>
                                    <Col xs={12} md={6}>{kpi("Annulées", counts?.annuleCount, "#6b7280", <StopOutlined />)}</Col>
                                </Row>

                                <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                    <Table
                                        rowKey="key"
                                        columns={columns}
                                        dataSource={data}
                                        scroll={{ x: "max-content" }}
                                        locale={{ emptyText: <Empty description="Aucune évaluation" /> }}
                                        pagination={{
                                            current: page, pageSize, total, showSizeChanger: true,
                                            showTotal: (t) => `${t} évaluation(s)`,
                                            onChange: (p, s) => { setPage(p); setPageSize(s); },
                                        }}
                                    />
                                </Card>
                            </Spin>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default ValidationEvaluationsV2;
